import { MouseEvent, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import ButtonBase from "@mui/material/ButtonBase";

import { AlertIcon, ExpandMoreIcon } from "@dotnet/telematics-design-system";

import { MenuAnchorEl, svgFontSize } from "shared";

import InputField from "../InputField";
import ListChoice from "../ListChoice";
import Menu from "../Menu";
import { useStyles } from "./hooks";
import { InputFieldWithDropdownProps } from "./types";

const InputFieldWithDropdown = ({
    dropdownList,
    selectedListItem,
    errorText,
    containerCustomStyles,
    onListChoiceClick,
    ...props
}: InputFieldWithDropdownProps) => {
    const { fullWidth, disabled, inputCustomStyle } = props;

    const { t } = useTranslation();

    const {
        buttonTitleStyles,
        inputStyles,
        menuStyles,
        errorTextStyles,
        containerStyles,
        buttonStyles
    } = useStyles();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const hasMenu = dropdownList.length > 1;

    const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);

    const openMenu = (e: MouseEvent<HTMLButtonElement>) =>
        setAnchorEl(e.currentTarget);

    const closeMenu = () => {
        setAnchorEl(null);

        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const onButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
        if (!disabled) {
            hasMenu ? openMenu(e) : inputRef.current?.focus();
        }
    };

    return (
        <div
            css={css({
                ...containerCustomStyles
            })}
        >
            <div
                css={css(
                    containerStyles(
                        anchorEl,
                        fullWidth,
                        disabled,
                        Boolean(errorText)
                    )
                )}
            >
                <ButtonBase
                    ref={buttonRef}
                    onClick={onButtonClick}
                    disabled={disabled}
                    disableRipple
                    sx={buttonStyles(hasMenu, disabled)}
                >
                    <span css={css(buttonTitleStyles)}>
                        {t(
                            `Search##${selectedListItem?.name ?? dropdownList[0].name}`
                        )}
                    </span>

                    {hasMenu && <ExpandMoreIcon />}
                </ButtonBase>

                <InputField
                    {...props}
                    inputRef={inputRef}
                    inputCustomStyle={{ ...inputStyles, ...inputCustomStyle }}
                />
            </div>

            {errorText && (
                <p css={css(errorTextStyles)}>
                    <AlertIcon css={css({ ...svgFontSize(16) })} /> {errorText}
                </p>
            )}

            <Menu
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                anchorEl={anchorEl}
                css={css(menuStyles)}
            >
                {dropdownList.map(item => {
                    const { name, value } = item;
                    const isSelected = value === selectedListItem?.value;

                    return (
                        <ListChoice
                            key={value}
                            title={t(`Search##${name}`)}
                            selected={isSelected}
                            onClick={() => {
                                !isSelected && onListChoiceClick?.(item);
                                closeMenu();
                            }}
                        />
                    );
                })}
            </Menu>
        </div>
    );
};

export default InputFieldWithDropdown;
